import { useSwitchChain, useAccount, useWalletClient } from "wagmi";
import { polygon } from "viem/chains";
import { useToast } from "@/components/ui/use-toast";

export const useNetworkSwitch = () => {
  const { toast } = useToast();
  const { switchChainAsync } = useSwitchChain();
  const { data: walletClient } = useWalletClient();
  const { connector, chainId: connectedChainId } = useAccount();

  const getActualChainId = async () => {
    if (connector) {
      const provider = await connector.getProvider() as {
        request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      };
      if (provider.request) {
        const providerChainId = await provider.request({ method: "eth_chainId" });
        if (typeof providerChainId === "string" || typeof providerChainId === "number") {
          return Number(providerChainId);
        }
      }
    }
    if (walletClient) return walletClient.getChainId();
    return connectedChainId;
  };

  const ensureCorrectNetwork = async () => {
    let currentChainId = connectedChainId;
    try {
      currentChainId = await getActualChainId();
    } catch {
      // Fall back to Wagmi's reactive chain when the connector cannot answer.
    }
    if (currentChainId !== polygon.id) {
      try {
        toast({
          title: "Wrong Network",
          description: "Switching to Polygon network...",
        });
        
        await switchChainAsync({ chainId: polygon.id });
        let walletChainId = await getActualChainId();
        for (let attempt = 0; walletChainId !== polygon.id && attempt < 10; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          walletChainId = await getActualChainId();
        }
        if (walletChainId !== polygon.id) throw new Error(`Wallet remained on chain ${walletChainId}`);
        return true;
      } catch (error) {
        toast({
          title: "Network Switch Failed",
          description: "Please manually switch to the Polygon network in your wallet",
          variant: "destructive",
        });
        return false;
      }
    }
    return true;
  };

  return { ensureCorrectNetwork };
};
